import React from 'react';
import { observer } from 'mobx-react-lite';

import mean from 'lodash/mean';
import findLast from 'lodash/findLast';
import round from 'lodash/round';
import sortBy from 'lodash/sortBy';

import eachDay from 'date-fns/each_day';
import format from 'date-fns/format';
import startOfDay from 'date-fns/start_of_day';

import { ComposedChart, Line, Label, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { DefaultTooltipContent } from 'recharts/lib/component/DefaultTooltipContent';

import { ChartColours } from '../../ChartColours';
import ObservableAutoSizer from '@vromo/ui/components/ObservableAutoSizer';
import { formatMinutes } from './helpers';
import { Job } from 'models';
import { AxisDomain } from 'recharts/types/util/types';

const CustomTooltip = (props: any) => {
    if (props.payload && props.payload[0] != null) {
        const newPayload = [
            props.payload[props.payload.length - 1],
            {
                color: ChartColours[3],
                dataKey: 'Total Time',
                name: 'Total Time',
                value: formatMinutes(
                    (
                        Number(props.payload[0].payload['Time to Dispatch']) +
                        Number(props.payload[0].payload['Time to Start']) +
                        Number(props.payload[0].payload['Time on Job'])
                    ).toFixed(2)
                )
            },
            ...props.payload
                .slice(0, 3)
                .reverse()
                .map((p: any) => ({
                    ...p,
                    value: formatMinutes(p.value.toFixed(2))
                }))
        ];

        return (
            <DefaultTooltipContent
                {...props}
                payload={newPayload}
                labelFormatter={(label: number) => format(new Date(label), 'dddd Do MMMM YYYY')}
            />
        );
    }
    return <DefaultTooltipContent {...props} />;
};

interface EndToEndJobDurationChartProps {
    jobs: Job[];
    startDate: Date;
    endDate: Date;
}

export default observer<EndToEndJobDurationChartProps>(function EndToEndJobDurationChart({
    jobs,
    startDate,
    endDate
}) {
    const dates = eachDay(startDate, endDate);

    const data = dates.map((d) => {
        const dispatchTimes: number[] = [];
        const startTimes: number[] = [];
        const jobTimes: number[] = [];

        const jobsForDate = jobs.filter((j) => {
            return j.status === 'completed' && startOfDay(j.finished!).getTime() == d.getTime();
        });

        jobsForDate.forEach((j) => {
            const dispatches = sortBy(j.dispatches!.items, ['created']);

            const dispatched = dispatches.find((it) => it.type == 'dispatched')?.created || 0;
            const lastUnderway = findLast(dispatches, (it) => it.type == 'underway')?.created || 0;

            const dispatchTime = dispatched - j.created!;
            const startTime = lastUnderway - dispatched;
            const jobTime = j.finished! - lastUnderway;

            dispatchTime ? dispatchTimes.push(dispatchTime) : null;
            startTime ? startTimes.push(startTime) : null;
            jobTime ? jobTimes.push(jobTime) : null;
        });

        const timeToStart = startTimes.length ? mean(startTimes) / (1000 * 60) : 0;
        const timeToDispatch = dispatchTimes.length ? mean(dispatchTimes) / (1000 * 60) : 0;
        const timeOnJob = jobTimes.length ? mean(jobTimes) / (1000 * 60) : 0;

        return {
            date: format(d, 'DD MMMM YYYY'),
            'Jobs Completed': jobsForDate.length,
            'Total Time': timeToStart + timeToDispatch + timeOnJob,
            'Time to Start': timeToStart,
            'Time to Dispatch': timeToDispatch,
            'Time on Job': timeOnJob
        };
    });

    const domain: AxisDomain = [0, (dataMax: number) => round(dataMax + 10, -1)];

    return (
        <ObservableAutoSizer customChildren={({ width }: { width: number }) => (
            <ComposedChart
                width={width}
                height={220}
                data={data}
                margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5
                }}>
                <CartesianGrid strokeDasharray="3 5" />
                <XAxis
                    dataKey="date"
                    minTickGap={90}
                    tickFormatter={(t) => format(new Date(t), 'Do MMM')}
                />
                <YAxis
                    yAxisId="left"
                    orientation="left"
                    type="number"
                    dataKey="Total Time"
                    domain={domain}>
                    <Label
                        value={'Time (mins)'}
                        angle={-90}
                        position={'insideLeft'}
                        offset={15}
                        style={{ textAnchor: 'middle', fontSize: '11px' }}
                    />
                </YAxis>
                <YAxis
                    yAxisId="right"
                    type="number"
                    dataKey="Jobs Completed"
                    orientation="right"
                    domain={domain}>
                    <Label
                        value={'Jobs Completed'}
                        angle={90}
                        position={'insideRight'}
                        offset={15}
                        style={{ textAnchor: 'middle', fontSize: '11px' }}
                    />
                </YAxis>
                <Tooltip content={<CustomTooltip />} />
                <Bar
                    dataKey="Time to Dispatch"
                    stackId="a"
                    fill={ChartColours[6]}
                    yAxisId="left"
                />
                <Bar
                    dataKey="Time to Start"
                    stackId="a"
                    fill={ChartColours[5]}
                    yAxisId="left"
                />
                <Bar dataKey="Time on Job" stackId="a" fill={ChartColours[1]} yAxisId="left" />
                <Line
                    type="monotone"
                    dataKey="Jobs Completed"
                    stroke={ChartColours[4]}
                    yAxisId="right"
                    dot={data.length > 60 ? false : true}
                />
            </ComposedChart>
        )} />
    );
});
